import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { useEffect, useState } from "react";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ButtonGroup,
  ConfirmButton,
  Row,
  CancelButton,
  InputContainer,
  Label,
  FolderListContainer,
  FolderItem,
  FolderName,
  FavoriteFolderItem,
  CreateNewItem,
} from "./AddToFolderModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";

import LottieIcon from "../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/checkBox.json";
import { selectUser } from "../../store/slices/user/slice";
import { useSelector } from "react-redux";
import { selectToggleToFavoriteLoading } from "../../store/slices/model/slice";

const FAVORITE_FOLDER_ID = "favorite";

/* Inline heart icon for the Favorites row — no extra asset import needed. */
const HeartIcon = ({ $filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 20.5s-7.5-4.78-7.5-10.25A4.25 4.25 0 0 1 12 6a4.25 4.25 0 0 1 7.5 4.25C19.5 15.72 12 20.5 12 20.5Z"
      fill={$filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

/* Inline folder icon with the same stroke-driven rendering as the rest. */
const FolderGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M3 8a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const AddToFolderModal = ({
  isOpen,
  onClose,
  isCreatingNew,
  setIsCreatingNew,
  onConfirm,
  closeCollectionModal,
  folders = [],
  openCreateFolderModal,
  isLoading = false,
  selectedCollectionFolders = [],
  video,
}) => {
  const [selectedFolders, setSelectedFolders] = useState([]);
  const user = useSelector(selectUser);
  const loading = useSelector(selectToggleToFavoriteLoading);

  useLockBodyScroll(isOpen);

  const isFavorite = user?.favoriteCollections?.includes(video?._id);

  useEffect(() => {
    if (selectedCollectionFolders) {
      const formattedFolders = folders.map((f) => f._id);
      const filteredFolders = selectedCollectionFolders.filter((f) =>
        formattedFolders.includes(f),
      );

      const updatedFolders = isFavorite
        ? [FAVORITE_FOLDER_ID, ...filteredFolders]
        : filteredFolders;

      setSelectedFolders(updatedFolders);
    }
  }, [folders, selectedCollectionFolders, isFavorite]);

  const handleFolderClick = (folderId) => {
    if (folderId === "create_new") {
      setIsCreatingNew(true);
      openCreateFolderModal();
      onClose();
      setSelectedFolders(selectedCollectionFolders);
    } else if (folderId === FAVORITE_FOLDER_ID) {
      setIsCreatingNew(false);
      setSelectedFolders((prev) =>
        prev.includes(FAVORITE_FOLDER_ID)
          ? prev.filter((id) => id !== FAVORITE_FOLDER_ID)
          : [FAVORITE_FOLDER_ID, ...prev],
      );
    } else {
      setIsCreatingNew(false);
      setSelectedFolders((prev) =>
        prev.includes(folderId)
          ? prev.filter((id) => id !== folderId)
          : [...prev, folderId],
      );
    }
  };

  const handleConfirm = () => {
    const wasFavoriteSelected = selectedFolders.includes(FAVORITE_FOLDER_ID);
    const regularFolders = selectedFolders.filter(
      (id) => id !== FAVORITE_FOLDER_ID,
    );

    const shouldToggleFavorite = wasFavoriteSelected !== isFavorite;

    onConfirm({
      folderIds: regularFolders,
      toggleFavorite: shouldToggleFavorite,
    });
  };

  const handleCancel = () => {
    if (!isLoading && !loading) {
      const updatedFolders = isFavorite
        ? [FAVORITE_FOLDER_ID, ...selectedCollectionFolders]
        : selectedCollectionFolders;
      setSelectedFolders(updatedFolders);
      setIsCreatingNew(false);
      onClose();
    }
  };

  const wasFavoriteSelected = selectedFolders.includes(FAVORITE_FOLDER_ID);
  const regularFolders = selectedFolders.filter(
    (id) => id !== FAVORITE_FOLDER_ID,
  );

  const hasFavoriteChange = wasFavoriteSelected !== isFavorite;
  const hasRegularFolderChanges =
    JSON.stringify(regularFolders.sort()) !==
    JSON.stringify([...selectedCollectionFolders].sort());

  const hasAnyChanges = hasFavoriteChange || hasRegularFolderChanges;
  const isConfirmDisabled = isLoading || loading || !hasAnyChanges;

  const isFavoriteSelected = selectedFolders.includes(FAVORITE_FOLDER_ID);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        <ModalContent>
          {/* Header — title left, close right */}
          <Row>
            <ModalTitle>Add to Folder</ModalTitle>
            <CloseButton onClick={handleCancel} aria-label="Close">
              <CloseSvg width={16} height={16} />
            </CloseButton>
          </Row>

          {/* Folder selection area */}
          <InputContainer>
            <Label>Your Folders</Label>

            <FolderListContainer $paddingRight={folders?.length < 6}>
              {/* Favorites — distinct rose-tinted card with heart icon */}
              <FavoriteFolderItem
                onClick={() => handleFolderClick(FAVORITE_FOLDER_ID)}
                $isSelected={isFavoriteSelected}
              >
                <HeartIcon $filled={isFavoriteSelected} />
                <FolderName>Favorites</FolderName>
                <LottieIcon
                  animationData={checkboxAnimation}
                  trigger="toggle-reverse"
                  isActive={isFavoriteSelected}
                  onActiveChange={() => handleFolderClick(FAVORITE_FOLDER_ID)}
                  onClick={() => handleFolderClick(FAVORITE_FOLDER_ID)}
                  strokeColor={"#d34763"}
                  speed={2}
                  size={24}
                />
              </FavoriteFolderItem>

              {/* Regular folder cards */}
              {folders &&
                folders?.length > 0 &&
                folders.map((folder) => {
                  const selected = selectedFolders.includes(folder._id);
                  return (
                    <FolderItem
                      key={folder._id}
                      onClick={() => handleFolderClick(folder._id)}
                      $isSelected={selected}
                    >
                      <FolderGlyph />
                      <FolderName>{folder.name}</FolderName>

                      <LottieIcon
                        animationData={checkboxAnimation}
                        trigger="toggle-reverse"
                        isActive={selected}
                        onActiveChange={() => handleFolderClick(folder._id)}
                        onClick={() => handleFolderClick(folder._id)}
                        strokeColor={"var(--color-brand-500)"}
                        speed={2}
                        size={24}
                      />
                    </FolderItem>
                  );
                })}
            </FolderListContainer>

            {/* Create new folder — prominent dashed CTA card below the list */}
            <CreateNewItem
              onClick={() => handleFolderClick("create_new")}
              $isSelected={isCreatingNew}
            >
              <PlusCircleIcon />
              <FolderName>Create new folder</FolderName>
              <span style={{ width: 24, height: 24 }} />
            </CreateNewItem>
          </InputContainer>

          {/* Action row */}
          <ButtonGroup>
            <CancelButton
              onClick={handleCancel}
              disabled={isLoading || loading}
            >
              Cancel
            </CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
            >
              {isLoading || loading ? (
                <l-bouncy size="18" stroke="2" speed="1" color="#fff" />
              ) : (
                "Add"
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default AddToFolderModal;
